/**
 * Change of Registered Office Address 전용 variable mapper
 *
 * 이 mapper 는 PreSetChangeAddressPanel 에서 수집한 입력을
 * Change Address 관련 템플릿이 사용하는 정확한 변수명(`[[...]]`)으로 펼쳐준다.
 *
 * 변수명 매칭은 case-sensitive 이며 (documentVariableReplacer.js),
 * 실제 docx 템플릿에서 관찰된 변수와 일반적인 alias 를 모두 포함한다.
 */

/** Governing document type — DCR 의 resolution_basis_* 분기 (AOD/AGM 과 동일). */
export type GoverningDocumentType = 'constitution' | 'articles' | 'third_schedule';

export interface AddressStructured {
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  country?: string;
  state?: string;
  city?: string;
  postcode?: string;
}

export interface ChangeAddressInput {
  companyName: string;
  registrationNo: string;
  oldAddressComposed: string;
  oldAddressStructured: AddressStructured;
  newAddressComposed: string;
  newAddressStructured: AddressStructured;
  dateOfChange?: string;      // e.g. '2026-04-21'
  dateOfDeclaration?: string; // e.g. '2026-04-21'
  dateOfResolution?: string;  // Directors' Circular Resolution 날짜
  clauseNo?: string;          // 정관 조항 번호 (숫자만, e.g. '106', '15(d)')
  articleNo?: string;         // 정관 전체 텍스트 (참고용)
  isSoleDirector?: boolean;   // (legacy) DCR [[if isSoleDirector]]I[[else]]We[[endif]] / sole director vs directors
  hasConstitution?: boolean;  // (legacy) DCR [[if has_constitution]]Clause…[[else]]Paragraph 15 Third Schedule…[[endif]]

  // ── 새 DCR 모델 (AOD/AGM 과 동일한 거버닝-문서 분기) ──
  //   resolution_basis_constitution / resolution_basis_articles / resolution_basis_third_schedule
  governingDocumentType?: GoverningDocumentType;  // 기본값 'constitution'
  directorCount?: 'single' | 'multiple';          // is_sole_director_company / is_multiple_director_company (없으면 isSoleDirector 폴백)
  constitutionClauseNumber?: string;              // constitution_clause_number (없으면 clauseNo 폴백)

  // 날짜 skip(X) — skip 된 날짜는 blank 로 lodge (기본 fallback 무시). AOD 의 valOrBlank 와 동일 정책.
  skippedDates?: {
    dateOfChange?: boolean;
    dateOfDeclaration?: boolean;
    dateOfResolution?: boolean;
  };
}

function trim(s: any): string {
  return s === null || s === undefined ? '' : String(s).trim();
}

/**
 * "Clause 59 of the ..." / "Article 15(d) of ..." / "59" 어떤 형태든
 * 템플릿 [[clause_no]] 에는 식별자(숫자/조항 번호)만 남기도록 strip.
 * 백엔드가 못 잡은 케이스에 대한 안전망.
 */
function stripClausePrefix(v: string): string {
  const s = trim(v);
  if (!s) return '';
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  if (m1) return m1[1];
  const m2 = s.match(/\b(\d+[\w()]*)/);
  return m2 ? m2[1] : s;
}

export function buildChangeAddressVariables(input: ChangeAddressInput): Record<string, any> {
  const companyName = trim(input.companyName);
  const regNo = trim(input.registrationNo);
  const oldAddr = trim(input.oldAddressComposed);
  const newAddr = trim(input.newAddressComposed);
  // skip(X) 된 날짜는 blank 로 lodge — fallback 보다 우선.
  const skip = input.skippedDates || {};
  const dateOfChange = skip.dateOfChange ? '' : trim(input.dateOfChange);
  // Date of Declaration 미입력 시 Date of Change of Address 로 폴백 (단일 날짜 입력 UX). skip 이면 blank.
  const dateOfDeclaration = skip.dateOfDeclaration ? '' : (trim(input.dateOfDeclaration) || dateOfChange);
  const dateOfResolution = skip.dateOfResolution ? '' : (trim(input.dateOfResolution) || dateOfDeclaration || dateOfChange);
  const clauseNo = stripClausePrefix(input.clauseNo || '');
  const articleNo = trim(input.articleNo);

  // ── 거버닝-문서 분기 (AOD/AGM 동일) ──
  //   govType 미지정 시 legacy 폴백: hasConstitution=false → constitution 유지(번호 없으면 빈 clause).
  const govType: GoverningDocumentType = input.governingDocumentType || 'constitution';
  //   constitution_clause_number 는 별도 지정값 우선, 없으면 clauseNo. Third Schedule 은 번호 없음.
  const constitutionClauseNumber =
    govType === 'third_schedule'
      ? ''
      : stripClausePrefix(input.constitutionClauseNumber || '') || clauseNo;
  //   단/복수 director — directorCount 우선, 없으면 legacy isSoleDirector 폴백.
  const isSingle = input.directorCount ? input.directorCount === 'single' : !!input.isSoleDirector;
  //   결의 근거 완성 문구 (3종, Third Schedule 포함) — 단일 토큰 [[resolution_basis_clause]] 용.
  const resolutionBasisClause =
    govType === 'third_schedule'
      ? 'Paragraph 15 of the Third Schedule of the Companies Act 2016'
      : govType === 'articles'
        ? (constitutionClauseNumber
            ? `Article ${constitutionClauseNumber} of the Company's Constitution (Articles of Association)`
            : "the Company's Constitution (Articles of Association)")
        : (constitutionClauseNumber
            ? `Clause ${constitutionClauseNumber} of the Company's Constitution`
            : "the Company's Constitution");

  const newS = input.newAddressStructured || {};
  const oldS = input.oldAddressStructured || {};

  return {
    // ── 템플릿이 실제로 사용하는 snake_case 변수 (Section 46(3).docx / Directors' Circular Resolution) ──
    company_name: companyName,
    company_registration_no: regNo,
    new_registered_address: newAddr,
    old_registered_address: oldAddr,
    date_of_change_of_address: dateOfChange,
    Date_of_Declaration: dateOfDeclaration,
    date_of_declaration: dateOfDeclaration, // ★ 템플릿 변수 추출 시 varKey가 소문자 정규화되므로 lowercase alias 필요
    Date_of_Resolution: dateOfResolution,
    date_of_resolution: dateOfResolution,

    // ── 정관 조항 (Directors' Circular Resolution: "pursuant to Clause [[clause_no]] ...") ──
    clause_no: clauseNo,
    Clause_No: clauseNo,
    article_no: articleNo,

    // ── (legacy) DCR 인라인 조건문 분기 (truthy='true' / falsy='') ──
    //   [[if isSoleDirector]]I[[else]]We[[endif]] / [[if has_constitution]]Clause [[clause_no]] … Constitution[[else]]Paragraph 15 … Third Schedule …[[endif]]
    isSoleDirector: isSingle ? 'true' : '',
    is_sole_director: isSingle ? 'true' : '',
    has_constitution: govType !== 'third_schedule' ? 'true' : '',

    // ── 새 DCR 모델 (AOD/AGM 동일) — Directors' Circular Resolution ──
    //   [[if is_sole_director_company]]I, the undersigned, being the sole director …[[endif]]
    //   [[if is_multiple_director_company]]We, the directors …[[endif]]
    is_sole_director_company: isSingle,
    is_multiple_director_company: !isSingle,
    //   [[if resolution_basis_constitution]]Clause [[constitution_clause_number]] of the Company's Constitution[[endif]]
    //   [[if resolution_basis_articles]]Article [[constitution_clause_number]] of the Company's Constitution (Articles of Association)[[endif]]
    //   [[if resolution_basis_third_schedule]]Paragraph 15 of the Third Schedule of the Companies Act 2016[[endif]]
    resolution_basis_constitution: govType === 'constitution',
    resolution_basis_articles: govType === 'articles',
    resolution_basis_third_schedule: govType === 'third_schedule',
    constitution_clause_number: constitutionClauseNumber,
    //   3종 모두 채워지는 단일 토큰 (Third Schedule 포함) — 조건 블록 대신 쓸 때.
    resolution_basis_clause: resolutionBasisClause,
    governing_document_clause: resolutionBasisClause,
    //   legacy boolean alias (조건문 엔진이 boolean 도 지원)
    constitution: govType === 'constitution',
    articles: govType === 'articles',
    third_schedule: govType === 'third_schedule',

    // ── 신주소 세부 필드 (템플릿에서 개별 라인/도시/주 등을 쓰는 경우) ──
    new_address_line_1: trim(newS.addressLine1),
    new_address_line_2: trim(newS.addressLine2),
    new_address_line_3: trim(newS.addressLine3),
    new_city: trim(newS.city),
    new_state: trim(newS.state),
    new_postcode: trim(newS.postcode),
    new_country: trim(newS.country) || 'MALAYSIA',

    // ── 구주소 세부 필드 ──
    old_address_line_1: trim(oldS.addressLine1),
    old_address_line_2: trim(oldS.addressLine2),
    old_address_line_3: trim(oldS.addressLine3),
    old_city: trim(oldS.city),
    old_state: trim(oldS.state),
    old_postcode: trim(oldS.postcode),
    old_country: trim(oldS.country),

    // ── UPPER_CASE 대안 alias (레거시 템플릿 호환) ──
    COMPANY_NAME: companyName,
    NAME_OF_COMPANY: companyName,
    COMPANY_REGISTRATION_NO: regNo,
    REGISTRATION_NUMBER: regNo,
    OLD_REGISTERED_ADDRESS: oldAddr,
    OLD_REGISTERED_OFFICE_ADDRESS: oldAddr,
    NEW_REGISTERED_ADDRESS: newAddr,
    NEW_REGISTERED_OFFICE_ADDRESS: newAddr,
    DATE_OF_CHANGE_OF_ADDRESS: dateOfChange,
    DATE_OF_DECLARATION: dateOfDeclaration,
    DATE_OF_RESOLUTION: dateOfResolution,
    CLAUSE_NO: clauseNo,
    ARTICLE_NO: articleNo,
  };
}
